import { get, writable, type Readable } from 'svelte/store';
import { createActivityTag, deleteActivityTag, getActivityTagOptions, type CustomActivityTag } from '@/Util/api';

export type { CustomActivityTag };

// Colours offered when creating a custom tag. Each is saturated enough that a darkened shade of it
// reads well over a faded wash of it (see tagStyle()).
export const CUSTOM_TAG_COLORS = [
  '#64748b', // slate
  '#dc2626', // red
  '#ea580c', // orange
  '#ca8a04', // yellow
  '#16a34a', // green
  '#0d9488', // teal
  '#2563eb', // blue
  '#7c3aed', // violet
  '#db2777', // pink
];

// Most tags one activity can carry. Kept in sync with Activity::MAX_CUSTOM_TAGS in app/Activity.php;
// Activity::addCustomTag() refuses a tag past it.
export const MAX_TAGS_PER_ACTIVITY = 20;

// Kept in sync with DEFAULT_CUSTOM_TAG_COLOR in ActivityController.php.
export const DEFAULT_CUSTOM_TAG_COLOR = CUSTOM_TAG_COLORS[0];

// The four tags every athlete starts with. The names are kept in sync with STANDARD_ACTIVITY_TAGS
// in app/Http/Controllers/ActivityController.php, which rejects a custom tag that duplicates one of
// these.
// Race is a raspberry and Event a golden amber: far apart in hue (the old red and orange blurred together), muted to sit with the app's teal and navy, and both clear of Workout's blue and Commute's teal.
export const STANDARD_ACTIVITY_TAGS: CustomActivityTag[] = [
  { name: 'Race', color: '#b8325a' },
  { name: 'Event', color: '#c27d0e' },
  { name: 'Workout', color: '#2563eb' },
  { name: 'Commute', color: '#0d9488' },
];

export type RaceEventKind = 'Race' | 'Event';

/** The Race and Event pills' colours, for the target icons on the Seasons and Progression charts. */
export const RACE_EVENT_COLORS: Record<RaceEventKind, string> = {
  Race: STANDARD_ACTIVITY_TAGS.find((tag) => tag.name === 'Race')!.color,
  Event: STANDARD_ACTIVITY_TAGS.find((tag) => tag.name === 'Event')!.color,
};

/**
 * Inline style for a tag pill, done the same way as the blue "Group" tag (the `session` variant in
 * ActivityTag.ts): a faded 20% wash of the tag's colour behind a darker, more saturated shade of it
 * for the text. The colour dots elsewhere use the tag's colour as-is.
 */
export function tagStyle(color: string): string {
  return `color: ${darken(color, 0.25)}; background-color: ${color}33;`;
}

/** `#rrggbb` mixed toward black by `amount` (0-1). */
function darken(color: string, amount: number): string {
  const channels = [1, 3, 5].map((i) => Math.round(parseInt(color.slice(i, i + 2), 16) * (1 - amount)));
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

export type ActivityTagOptions = {
  standard: CustomActivityTag[];
  custom: CustomActivityTag[];
};

/**
 * Every tag on offer -- standard first, then the athlete's custom ones in the order added. An
 * activity can still carry a tag that isn't in here (a custom tag deleted since it was applied, or
 * another athlete's on a squad dashboard); it just has no colour.
 */
export function allTags(options: ActivityTagOptions): CustomActivityTag[] {
  return [...options.standard, ...options.custom].filter(
    (tag, index, all) => all.findIndex((other) => other.name === tag.name) === index
  );
}

/** Tag name -> colour, for every tag on offer. */
export function tagColors(options: ActivityTagOptions): Map<string, string> {
  return new Map(allTags(options).map((tag) => [tag.name, tag.color]));
}

/**
 * The tags an athlete can apply to an activity -- fetched once and shared by every mounted
 * CustomActivityTags control and the dashboard's tag filter, rather than each card requesting its
 * own copy of the same list.
 */
const store = writable<ActivityTagOptions>({ standard: STANDARD_ACTIVITY_TAGS, custom: [] });
let loaded = false;
let loading: Promise<void> | null = null;

function load(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (!loading) {
    loading = getActivityTagOptions()
      .then(({ custom }) => {
        store.set({ standard: STANDARD_ACTIVITY_TAGS, custom });
        loaded = true;
      })
      .catch(() => {
        // Leave the store at its standard-only default; whatever asked for tags still gets those.
        // `loaded` stays false, so the next control to mount tries again.
      })
      .finally(() => {
        loading = null;
      });
  }
  return loading;
}

/** Kicks off the fetch (idempotent) and hands back the store to read/subscribe to. */
export function activityTagOptions(): Readable<ActivityTagOptions> {
  load();
  return store;
}

/** ASCII-only case folding, to match the server's strcasecmp() duplicate check. */
const asciiLower = (value: string) => value.replace(/[A-Z]/g, (c) => c.toLowerCase());

/** The spelling of the tag on offer that `name` duplicates (any case), if there is one. */
function existingTagName(name: string, options: ActivityTagOptions): string | undefined {
  return allTags(options).find((tag) => asciiLower(tag.name) === asciiLower(name))?.name;
}

/**
 * Adds a new custom tag to the athlete's persisted list, and to the shared store. A name that is
 * already on offer in any case ("race" when "Race" exists) adds nothing and comes back as
 * `duplicateOf` -- checked here first, and again by the server in case another tab added it.
 */
export async function addCustomActivityTag(name: string, color: string): Promise<{ duplicateOf?: string }> {
  const trimmed = name.trim();
  const known = existingTagName(trimmed, get(store));
  if (known) return { duplicateOf: known };
  const { custom, existing } = await createActivityTag({ name: trimmed, color });
  store.update((options) => ({ ...options, custom }));
  return existing ? { duplicateOf: existing } : {};
}

/**
 * Set whenever one tag is saved onto or off an activity, so a list showing it (the activity
 * dashboard) can update its copy and drop a card that no longer matches a tag filter -- a tag edit
 * doesn't raise the server's modify_activity_complete event the dashboard otherwise listens for.
 * One change rather than the resulting list, so applying it can't undo another edit still saving.
 */
export type ActivityTagChange = { path: string, tag: string, added: boolean };
export const activityTagsSaved = writable<ActivityTagChange | null>(null);

/** `tags` with one change applied. */
export function applyTagChange(tags: string[], change: ActivityTagChange): string[] {
  if (!change.added) return tags.filter((tag) => tag !== change.tag);
  return tags.includes(change.tag) ? tags : [...tags, change.tag];
}

/**
 * Deletes a custom tag from the athlete's list of tags on offer, and from the shared store.
 * Activities already carrying it keep it -- an activity's tags are just names, not references.
 */
export async function removeCustomActivityTag(name: string): Promise<void> {
  const { custom } = await deleteActivityTag(name);
  store.update((options) => ({ ...options, custom }));
}
